import React from 'react';
import { Navigate, useParams } from 'react-router';
import { Box, CircularProgress } from '@mui/material';

import { useActionsDisplayMode } from '../auth/useActionsDisplayMode';
import { paths } from './paths';

/**
 * Actions-surface guard for a route's `actionsSurface` metadata (CPT-1376).
 *
 * Each tenant shows one or both of Actions (V1) and Task V2, per its
 * actionsDisplayMode. The sidebar already hides the surface(s) the tenant
 * doesn't have (via useActionsDisplayMode's derived taskV2Enabled /
 * hideV1Actions booleans, consumed unchanged by useSidebarNav); this closes
 * the direct-URL / deep-link gap so a hidden surface cannot be reached by
 * typing or pasting its path (AC7).
 *
 * Deliberately NOT an RBAC check — RequirePermission wraps this guard in
 * AppRoutes.tsx and is still enforced first either way (AC9: permissions are
 * unchanged). The tenant's actionsDisplayMode is a second, independent gate,
 * exactly mirroring how RequireStoryblokMode relates to permission checks.
 *
 * Presentation-only: redirecting away from a hidden surface never mutates
 * action data (AC8). Switching the tenant's mode back makes the surface (and
 * its actions) reachable again, unchanged.
 *
 * While the mode is resolving we render a spinner rather than guessing, same
 * reasoning as RequireStoryblokMode: a wrong guess could bounce a legitimate
 * visitor off the page they asked for.
 */
const Spinner: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
    <CircularProgress />
  </Box>
);

const RequireActionsDisplayMode: React.FC<{
  actionsSurface?: 'v1' | 'v2';
  children: React.ReactNode;
}> = ({ actionsSurface, children }) => {
  const { tenantCode } = useParams<{ tenantCode: string }>();
  const { mode, ready } = useActionsDisplayMode(tenantCode ?? '');

  if (!actionsSurface) return <>{children}</>;

  if (!ready) return <Spinner />;

  const v1Visible = mode === 'actions' || mode === 'both';
  const v2Visible = mode === 'both' || mode === 'task-v2';
  const allowed = actionsSurface === 'v1' ? v1Visible : v2Visible;
  if (allowed) return <>{children}</>;

  const target = tenantCode
    ? v1Visible
      ? paths.actions(tenantCode)
      : paths.taskV2Actions(tenantCode)
    : '/';
  return <Navigate to={target} replace />;
};

export default RequireActionsDisplayMode;
